import { gql } from "@apollo/client";
import { endOfDay, formatISO, startOfDay, subHours } from "date-fns";
import { useEffect, useReducer, useMemo, useState } from "react";

import { castDateToString } from "@caredna-marlin/utils/dates";
import { calculateDriveTimeOptimizations, combineCalendarBlocks, combineResources, CommandCenterReducerState, DriveTimePatch, findModifiedResourceIds, LocalCalendarBlock, LocalVisit, LocalVisitResource, LocalVisitResourceWithVisit, rootReducer } from '~/hooks/visitDragDropReducer';
import { useOrgConfig, OrgConfig } from "~/providers/OrgConfigProvider";
import { CommandIndexCombinedDataQuery, GetSimpleDriveTimeQueryVariables, useCommandIndexCareRequirementVisitsQuery, useCommandIndexCombinedDataQuery, useGetAvailableOrgsQuery, useGetSimpleDriveTimeLazyQuery, VisitResourceGenericInput } from "~/graphql/__generated__/graphql";
import useVisitRequests from "./useVisitRequests";
import { hasOverlap } from "@caredna-marlin/utils/client";
import { useOrganization } from "~/providers/GraphqlProvider";
import { useOrganizationalUnitSegmentationEnabled } from "./permissions";
import { visitResourceStatus } from "~/utils/statuses";

export function groupVisitResourcesByVisit(
  visitResources: LocalVisitResource[]
): LocalVisit[] {
  const visitMap = new Map<number, LocalVisit>();

  for (const resource of visitResources) {
    const { visit, ...resourceWithoutVisit } = resource;
    if (!visit) continue;

    if (!visitMap.has(visit.id)) {
      visitMap.set(visit.id, {
        ...visit,
        visitResources: [],
      });
    }

    visitMap.get(visit.id)?.visitResources.push(resourceWithoutVisit);
  }

  return Array.from(visitMap.values());
}

const initialState: CommandCenterReducerState = {
  modifiedVisitResources: {},
  newVisitResources: [],
  modifiedCalendarBlocks: {},
  dbVisitResources: [],
  dbShifts: [],
  dbCalendarBlocks: [],
  dbChangeDetected: false,
  driveUpdatesNeeded: {},
  currentUser: undefined,
  dbDateRange: undefined,
  visitMetaDataLookup: {},
  optimizerReviewData: {
    originalVisitResources: [],
    skippedVisitResources: [],
    assignmentIds: [],
  },
};

// This SHOULD BE a no-op, but we use it to inform typescript that even though the GQL query
// has visits nullable, all our LocalVisitResources have a visit defined. This makes dealing with
// them within the webapp much easier, by basically centralizing the null checking here
// rather than scattered around everywhere.
function nonEmptyVisit(visitResource: CommandIndexCombinedDataQuery["visitResource"][number]): visitResource is LocalVisitResource {
    return visitResource.visit !== null;
}

export type DriveTimeCompleteCallback = (patch: DriveTimePatch) => void;

const useVisitDragDrop = (date: Date, pollInterval: number) => {
  const orgConfig = useOrgConfig();
  const { organizationId } = useOrganization();
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();
  const [state, dispatch] = useReducer(rootReducer, initialState);
  
  const localVisitResources = useMemo(() => combineResources(state), [state]);
  const localCalendarBlocks = useMemo(() => combineCalendarBlocks(state), [state]);
  const modifiedResourceIds = useMemo(
    () => findModifiedResourceIds(localVisitResources, state.dbVisitResources),
    [localVisitResources, state.dbVisitResources]
  );
  const localShifts = state.dbShifts;
  const localVisits = useMemo(
    () => groupVisitResourcesByVisit(localVisitResources),
    [localVisitResources]
  );

  const driveTimeOptimizations = calculateDriveTimeOptimizations(state.optimizerReviewData.originalVisitResources, localVisitResources);

  const [fetchVisitDriveTimes, { error, loading: driveTimesLoading }] = useGetSimpleDriveTimeLazyQuery({
    fetchPolicy: "network-only",
  });

  const { data: currentUserData, loading: currentUserLoading } = useGetAvailableOrgsQuery();

  useEffect(() => {
    if (currentUserData?.currentUser.userId) {
      dispatch({ type: 'SET_CURRENT_USER', payload: currentUserData.currentUser.userId });
    }
  }, [currentUserData]);

  // Memoize queryVariables to prevent infinite loops in useEffect
  const queryVariables = useMemo(() => ({
    filterStartTime: formatISO(startOfDay(date)),
    filterEndTime: formatISO(endOfDay(date)),
    organizationalUnitSegmentationEnabled: organizationalUnitSegmentationEnabled === true,
  }), [date, organizationalUnitSegmentationEnabled]);
  const snapshotScope = `${organizationId}:${queryVariables.filterStartTime}:${queryVariables.filterEndTime}`;
  const [hydratedScope, setHydratedScope] = useState<string>();
  const careRequirementQueryVariables = useMemo(() => ({
    filterStartTime: formatISO(subHours(startOfDay(date), 24)),
    filterEndTime: formatISO(endOfDay(date)),
  }), [date]);

  const { data, error: dataError, loading: dataLoading } = useCommandIndexCombinedDataQuery({
    variables: queryVariables,
    fetchPolicy: "network-only",
    pollInterval,
    context: { queryDeduplication: false },
  });

  const { data: careRequirementVisitsData, loading: careRequirementVisitsLoading } = useCommandIndexCareRequirementVisitsQuery({
    variables: careRequirementQueryVariables,
    fetchPolicy: "network-only",
  });

  const localVisitRequests = useVisitRequests(data?.task || [], date);

  useEffect(() => {
    if (data && !dataLoading) {
      dispatch({
        type: 'SET_DB_RESOURCES',
        payload: {
          resources: data.visitResource.filter(nonEmptyVisit),
          shifts: data.shift,
          dbDateRange: queryVariables,
          blocks: data.calendarBlock,
        }
      });
      setHydratedScope(snapshotScope);
    }
  }, [data, dataLoading, queryVariables, snapshotScope])

  const [pendingDriveTimeShiftIds, setPendingDriveTimeShiftIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (Object.keys(state.driveUpdatesNeeded).length === 0 || !data?.shift) {
      setPendingDriveTimeShiftIds(new Set());
      return;
    }

    // Track which shifts have pending calculations
    const shiftIds = new Set(Object.keys(state.driveUpdatesNeeded));
    setPendingDriveTimeShiftIds(shiftIds);

    // Start all calculations in parallel
    const promises = Object.entries(state.driveUpdatesNeeded).map(async ([shiftId, requestTimestamp]) => {
      return recalculateDriveTimesFromState(
        shiftId,
        localVisitResources,
        localCalendarBlocks,
        localShifts,
        fetchVisitDriveTimes,
        orgConfig,
        requestTimestamp
      ).then((driveTimePatch) => {
        return driveTimePatch; // Return patch for batched dispatch
      });
    });

    Promise.all(promises).then((patches) => {
      const validPatches = patches.filter((p): p is DriveTimePatch => p !== undefined);
      validPatches.forEach(patch => {
        dispatch({ type: 'COMPLETE_DRIVE_TIME', payload: patch });
      });
      setPendingDriveTimeShiftIds(new Set());
    });
  }, [state.driveUpdatesNeeded, localShifts, localVisitResources, localCalendarBlocks, orgConfig, data?.shift, fetchVisitDriveTimes, dispatch]);
  const loading = dataLoading || currentUserLoading || (!dataError && hydratedScope !== snapshotScope);

  const localShiftLookup = Object.fromEntries(localShifts.map((shift) => [shift.id, shift]));
  const hasPendingDriveTimeUpdates = Object.keys(state.driveUpdatesNeeded).length > 0;

  return {
    dispatch,
    error,
    loading,
    driveTimesLoading,
    localVisitResources,
    modifiedResourceIds,
    modifiedCalendarBlocks: state.modifiedCalendarBlocks,
    dbVisitResources: state.dbVisitResources,
    dbCalendarBlocks: state.dbCalendarBlocks,
    localShifts,
    localShiftLookup,
    localCalendarBlocks,
    localVisits,
    localVisitRequests,
    visitMetaDataLookup: state.visitMetaDataLookup,
    dbChangeDetected: state.dbChangeDetected,
    newVisitResources: state.newVisitResources,
    originalVisitResources: state.optimizerReviewData.originalVisitResources ?? [],
    driveTimeOptimizations,
    skippedVisitResources: state.optimizerReviewData.skippedVisitResources ?? [],
    optimizerAssignmentIds: state.optimizerReviewData.assignmentIds ?? [],
    pendingDriveTimeShiftIds,
    hasPendingDriveTimeUpdates,
    careRequirementVisits: careRequirementVisitsData?.visit ?? [],
    careRequirementVisitsLoading,
  }
}

export default useVisitDragDrop;

export const recalculateDriveTimesFromState = async (
  shiftId: string,
  localVisitResources: LocalVisitResource[],
  localCalendarBlocks: LocalCalendarBlock[],
  shifts: CommandIndexCombinedDataQuery["shift"],
  fetchVisitDriveTimes: ReturnType<typeof useGetSimpleDriveTimeLazyQuery>[0],
  orgConfig: OrgConfig,
  requestTimestamp: number,
): Promise<DriveTimePatch | undefined> => {
  const completedWithNoChanges: DriveTimePatch = { timestamp: requestTimestamp, shiftId, patch: {} };
  const shift = shifts.find((candidate) => candidate.id.toString() === shiftId);
  // Still finish the request. A shift with nothing left on it (every visit skipped)
  // has no drive times to fetch, but an unfinished request blocks optimization review.
  if (!shift || !shift.provider?.id) {
    return completedWithNoChanges;
  }
  const shiftVisitResources = localVisitResources.filter((resource): resource is LocalVisitResourceWithVisit => {
    return resource.providerId == shift?.provider?.id &&
      resource.startTime >= shift.startTime &&
      resource.endTime <= shift.endTime &&
      resource.visit != null
  });

  // Filter calendar blocks for this provider and overlapping the shift
  const shiftCalendarBlocks = localCalendarBlocks.filter((block) =>
    block.providerId === shift.provider?.id &&
    block.eventStatus !== 'canceled' &&
    hasOverlap(block, shift)
  ).map((cb) => ({
    id: cb.id,
    eventKey: `calendarBlock-${cb.id}`,
    startTime: cb.startTime,
    endTime: cb.endTime,
    providerId: cb.providerId,
    addressCoordinates: cb.addressCoordinates,
    eventStatus: cb.eventStatus,
    startOfShiftDriveTime: cb.startOfShiftDriveTime,
    interVisitDriveTime: cb.interVisitDriveTime,
    endOfShiftDriveTime: cb.endOfShiftDriveTime,
  }));

  if (shiftVisitResources.length === 0 && shiftCalendarBlocks.length === 0) {
    return completedWithNoChanges;
  }

  const driveTimeVisitResources: VisitResourceGenericInput[] = shiftVisitResources.map((vr) => ({
    startTime: vr.startTime,
    endTime: vr.endTime,
    id: vr.id,
    eventKey: vr.id.toString(),
    providerId: vr.providerId,
    startOfShiftDriveTime: vr.startOfShiftDriveTime,
    endOfShiftDriveTime: vr.endOfShiftDriveTime,
    interVisitDriveTime: vr.interVisitDriveTime,
    inHome: orgConfig.visitResourceRequirements[vr.visitResourceRequirementId]?.inHome ?? true,
    addressCoordinates: vr.addressCoordinates,
    visit: {
      eventStatus: visitResourceStatus(vr),
      addressCoordinates: vr.visit.addressCoordinates,
    },
  }));

  // Create new drive times input
  const driveTimesInput: GetSimpleDriveTimeQueryVariables = {
    shift: {
      providerId: shift.provider.id,
      startTime: castDateToString(shift.startTime),
      endTime: castDateToString(shift.endTime),
      addressCoordinates: shift.addressCoordinates,
      useShiftCenterDriveTimes: shift.useShiftCenterDriveTimes,
      shiftCenter: shift.shiftCenterId ? {
        addressCoordinates: orgConfig.shiftCenters[shift.shiftCenterId]?.addressCoordinates || "",
      } : null,
    },
    providerAddress: {
      addressCoordinates: shift.provider?.addressCoordinates || "",
    },
    visitResources: driveTimeVisitResources,
    calendarBlocks: shiftCalendarBlocks,
  };

  // Calculate drive times
  const visitDriveTimeResponse = await fetchVisitDriveTimes({ variables: driveTimesInput });

  // Update drive times for all visit resources
  const driveTimePatch: DriveTimePatch = { timestamp: requestTimestamp, shiftId, patch: {} }
  const driveTimeUpdates = visitDriveTimeResponse?.data?.simpleDriveTime ?? [];
  for (const update of driveTimeUpdates) {
    if (update.eventKey) {
      driveTimePatch['patch'][update.eventKey] = {
        startOfShiftDriveTime: update.startOfShiftDriveTime ? parseInt(update.startOfShiftDriveTime) : 0,
        endOfShiftDriveTime: update.endOfShiftDriveTime ? parseInt(update.endOfShiftDriveTime) : 0,
        interVisitDriveTime: update.interVisitDriveTime ? parseInt(update.interVisitDriveTime) : 0,
      };
    }
  }

  return driveTimePatch
}

gql`
  query CommandIndexCombinedData($filterStartTime: String, $filterEndTime: String, $organizationalUnitSegmentationEnabled: Boolean = false)
  @permission(requires: useWebApp)
  {
    visitResource(where: { startTime: { gte: $filterStartTime, lt: $filterEndTime } }) {
      id
      providerId
      startTime
      endTime
      startOfShiftDriveTime
      interVisitDriveTime
      endOfShiftDriveTime
      visitId
      updatedBy
      provider {
        id
        firstName
        lastName
        providerTypeId
        active
      }
      virtualProviderStatus
      errandStatus
      visitResourceRequirementId
      addressCoordinates
      visit {
        id
        careRequirementId
        startTime
        endTime
        eventStatus
        addressStreet
        addressLineTwo
        addressCity
        addressState
        addressZip
        addressTimezone
        addressCoordinates
        arrivalWindowStartTime
        arrivalWindowEndTime
        createdAt
        visitWarningLogId
        updatedBy
        manualEdits
        cancellationReason {
          id
          description
        }
        patient {
          id
          firstName
          lastName
          addressCoordinates
          patientNotes
          serviceInstructions
          patientIdentifiers {
            identifier
            identifier_type
          }
          careTeamProviders {
            id
            allowAutomaticUpdates
            patientId
            providerId
            providerTypeId
          }
        }
        episode {
          id
          serviceLineId
          organizationalUnitId @include(if: $organizationalUnitSegmentationEnabled)
          autoschedulerGroup
          precautions {
            id
            precautionId
          }
        }
        visitTypeId
        eventLog(
          where: { visitResourceId: { isNull: true } }
          orderBy: { eventTime: { direction: desc, priority: 1 } }
          limit: 1
        ) {
          id
          event
          eventTime
          autoGenerated
          createdByUser {
            firstName
            lastName
          }
        }
      }
    }
    shift(
      where: {
        OR: [
          { startTime: { gte: $filterStartTime, lt: $filterEndTime } }
          { endTime: { gte: $filterStartTime, lt: $filterEndTime } }
        ]
      }
    ) {
      id
      status
      startTime
      endTime
      addressTimezone
      providerTravel
      manualEdits
      overflowShift
      providerId
      updatedBy
      provider {
        id
        firstName
        lastName
        addressCoordinates
        providerTypeId
        external
        active
      }
      addressCoordinates
      shiftCenterId
      useShiftCenterDriveTimes
    }
    calendarBlock(where: { startTime: { gte: $filterStartTime, lt: $filterEndTime } }) {
      id
      title
      startTime
      endTime
      eventStatus
      providerId
      startOfShiftDriveTime
      interVisitDriveTime
      endOfShiftDriveTime
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressCoordinates
      addressTimezone
      updatedBy
      blockType
      availabilityId
      manualEdits
      filters {
        visitTypeIds
      }
      provider {
        id
        firstName
        lastName
      }
    }

    task(
      where: {
        taskType: { eq: visit_request }
        status: { eq: pending }
      }
    ) {
      id
      taskType
      status
      description
      createdAt
      details
      patient {
        id
        firstName
        lastName
        addressStreet
        addressLineTwo
        addressCity
        addressState
        addressZip
        addressTimezone
        addressCoordinates
        patientNotes
        serviceInstructions
        patientIdentifiers {
          identifier
          identifier_type
        }
        careTeamProviders {
          id
          allowAutomaticUpdates
          patientId
          providerId
          providerTypeId
        }
      }
    }
  }
`;

gql`
  query GetSimpleDriveTime($shift: Shift!, $providerAddress: ProviderAddress!, $visitResources: [VisitResourceGenericInput!]!, $calendarBlocks: [CalendarBlockInput!]!)
  @permission(requires: createDragEvents)
  {
    simpleDriveTime(
      shift: $shift
      providerAddress: $providerAddress
      visitResources: $visitResources
      calendarBlocks: $calendarBlocks
    ) {
      id
      eventKey
      providerId
      startOfShiftDriveTime
      interVisitDriveTime
      endOfShiftDriveTime
    }
  }
`;

gql`
  query CommandIndexCareRequirementVisits($filterStartTime: String, $filterEndTime: String)
  @permission(requires: useWebApp)
  {
    visit(where: { 
      startTime: { gte: $filterStartTime, lt: $filterEndTime },
      eventStatus: { ne: canceled },
      careRequirementId: { ne: null }
    }) {
      id
      startTime
      patientId
      manualEdits
      careRequirement {
        id
        visitTypeId
        duration
        earliestStartTime
        latestStartTime
        minTimeFromLastVisit
        maxTimeFromLastVisit
      }
    }
  }
`;
